import {
    faArrowUpRightFromSquare,
    faCircleInfo,
    faDatabase,
    faHeadset,
    faInbox,
    faPhone,
    faRobot,
    faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';

import { withIcon } from '../../../core/components/Icon.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { useSessionStorage } from '../../../core/utils/hooks/useLocalStorage.ts';

const InfoIcon = withIcon(faCircleInfo);
const CloseIcon = withIcon(faXmark);
const DatabaseIcon = withIcon(faDatabase);
const RobotIcon = withIcon(faRobot);
const PhoneIcon = withIcon(faHeadset);
const InboxIcon = withIcon(faInbox);
const FooterPhoneIcon = withIcon(faPhone);
const ExternalLinkIcon = withIcon(faArrowUpRightFromSquare);

export type LockedAssistantKind = 'inboxAgent' | 'voiceAgent';

const PRODUCT_PAGE_URLS: Record<LockedAssistantKind, string> = {
    inboxAgent: 'https://www.workist.com/inbox-agent',
    voiceAgent: 'https://www.workist.com/voice-agent',
};

const LockedAssistantCard = ({ kind }: { kind: LockedAssistantKind }) => {
    const { t } = useTranslation('assistants');
    const [dismissed, setDismissed] = useSessionStorage(`assistants.locked.${kind}.dismissed`, false);
    if (dismissed) return null;

    const TileIcon = kind === 'voiceAgent' ? PhoneIcon : RobotIcon;
    const FooterIcon = kind === 'voiceAgent' ? FooterPhoneIcon : InboxIcon;
    const chips = t(`overview.locked.${kind}.chips`, { returnObjects: true }) as string[];

    return (
        <a
            href={PRODUCT_PAGE_URLS[kind]}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col gap-6 rounded-lg border border-solid border-line-weak bg-surface-weak-hover p-5 transition-colors hover:bg-surface-medium"
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-surface-agent-weak text-content-agent-weak">
                        <TileIcon />
                    </span>
                    <div className="flex flex-col">
                        <span className="text-sm font-semibold text-content-strong">
                            {t(`overview.locked.${kind}.title`)}
                        </span>
                        <span className="text-xs text-content-medium">{t(`overview.locked.${kind}.subtitle`)}</span>
                    </div>
                </div>
                <div className="flex flex-none items-center gap-2" onClick={(e) => e.preventDefault()}>
                    <span className="flex items-center gap-1 text-xs font-medium text-content-medium">
                        <InfoIcon />
                        {t('overview.locked.badge')}
                    </span>
                    <button
                        type="button"
                        onClick={() => setDismissed(true)}
                        aria-label={t('overview.locked.dismiss')}
                        className="cursor-pointer border-none bg-transparent p-0 text-content-weak hover:text-content-strong"
                    >
                        <CloseIcon />
                    </button>
                </div>
            </div>

            {chips.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {chips.map((chip) => (
                        <span
                            key={chip}
                            className={classnames(
                                'rounded-lg bg-surface-medium border border-line-weak px-2 py-0.5 text-xs font-medium text-content-strong'
                            )}
                        >
                            {chip}
                        </span>
                    ))}
                </div>
            )}

            <div className="mt-auto flex flex-col gap-3 text-xs text-content-medium">
                <span className="flex items-center gap-2">
                    <DatabaseIcon className="flex-none" />
                    {t(`overview.locked.${kind}.dataSourceLabel`)}
                </span>

                <span className="flex items-center gap-2">
                    <FooterIcon className="flex-none" />
                    {t(`overview.locked.${kind}.footer`)}
                    {' · '}
                    <span className="inline-flex items-center gap-1 font-medium text-content-brand-default">
                        {t('overview.locked.learnMore')}
                        <ExternalLinkIcon />
                    </span>
                </span>
            </div>
        </a>
    );
};

export default LockedAssistantCard;
